<script setup lang="ts">
import SearchBar from "~/components/Common/Inputs/SearchBar.vue";
import IndexLayout from "~/components/Indexs/IndexLayout.vue";
import IndexLayoutSkeleton from "~/components/Skeleton/IndexLayoutSkeleton.vue";
import { usePagination } from "~/composables/paginations/usePagination";
import { fetchWithStateHandling } from "~/composables/utils/useStateManager";
import type { IPaginationApi } from "~/utils/interfaces/table-interfaces";
import FooterActions from "~/components/Common/FooterActions.vue";
import { PaginationKeysEnum } from "~/utils/enums/pagination-keys.enums";
import { ContextFiltersEnum } from "~/utils/enums/context-filters.enums";
import { useLearningPathAttendeesStore } from "~/store/learning-path/learning-path-attendees.store";
import { useLearningPathAttendees } from "~/composables/learning-path/useLearningPathAttendees";
import AttendeesView from "./view/AttendeesView.vue";
import DeleteAttendeesModal from "../../../modal/attendees/DeleteAttendeesModal.vue";
import { useLearningPathAttendeeStore } from "~/store/learning-path/learning-path-attendee.store";
import EmptyState from "~/components/Common/EmptyState.vue";
import AppButton from "~/components/Common/Buttons/AppButton.vue";

const maxHeight = ref<string>("calc(100vh - 300px)");
const { headers, getNav, footersActionsAttendees } = useLearningPathAttendees();
const paginate = usePagination(PaginationKeysEnum.LEARNING_PATH_ATTENDEES_MODAL_PAGINATION);
const {
  handleLearningPathAddAttendeesModal,
  fetchLearningPathAttendeesFullDatas,
  resetLearningPathAttendeesMultiSelect,
  handleLearningPathDeleteAttendeesModal,
  deleteAttendeesFromLearningPath,
} = useLearningPathAttendeesStore();
const {
  learningPathAttendeesMultiSelect,
  learningPathAttendeesSelectAll,
  learningPathAttendeesFullDatas,
  learningPathDeleteAttendeesModal,
  currentLearningPathId,
} = storeToRefs(useLearningPathAttendeesStore());
const learningPathAttendeesStore = useLearningPathAttendeeStore();
const { handleLearningPathAttendee, setLearningPathAttendee } = learningPathAttendeesStore;
const searchMessage: Ref<string> = ref<string>("");
const showActionFooter = ref<boolean>(false);
const isLoadingRef = ref(true);
const isErrorRef = ref(true);
const visibleHeaders = computed(() => headers.value.filter((h) => h.visible));
const router = useRouter();
const route = useRoute();

paginate.setFetchFunction(async () => {
  const paginateOptions = paginate.getFindOptions(paginate.paginateOptions.value);
  await fetchLearningPathAttendeesFullDatas(paginateOptions, ContextFiltersEnum.LEARNING_PATH_ATTENDEES_MODAL_CONTEXT);
});

watch(
  () => learningPathAttendeesMultiSelect.value?.selectedItems,
  () => {
    if (learningPathAttendeesMultiSelect.value?.selectedItems?.length) {
      showActionFooter.value = true;
    } else if (
      !learningPathAttendeesMultiSelect.value?.selectedItems?.length &&
      !learningPathAttendeesSelectAll.value
    ) {
      showActionFooter.value = false;
    }
  },
);

async function loadDatas() {
  const { isLoading, isError } = await fetchWithStateHandling(async () => {
    await paginate.fetchData();
  });
  isLoadingRef.value = isLoading.value;
  isErrorRef.value = isError.value;
}

function handleAddAttendees() {
  resetLearningPathAttendeesMultiSelect();
  handleLearningPathAddAttendeesModal(true);
}

function handleCancelDeleteAttendees() {
  resetLearningPathAttendeesMultiSelect();
  handleLearningPathDeleteAttendeesModal(false);
}

const keepSubscriptions = ref(true);
async function handleKeepSubscriptions(keep: boolean) {
  keepSubscriptions.value = keep;

  if (!currentLearningPathId.value) return;

  await deleteAttendeesFromLearningPath(currentLearningPathId.value as string, keepSubscriptions.value);
  await paginate.fetchData();

  handleLearningPathDeleteAttendeesModal(false);
  resetLearningPathAttendeesMultiSelect();
}

function clickRow(row: any) {
  if (row.userId) {
    router.push({
      path: route.path,
      query: {
        ...route.query,
        attendee_id: row.userId,
      },
    });
    setLearningPathAttendee(row);
    handleLearningPathAttendee(true);

    resetLearningPathAttendeesMultiSelect();
  }
}

const disableChoiceActions = computed(() => {
  if (
    learningPathAttendeesFullDatas.value?.data?.length &&
    learningPathAttendeesFullDatas.value?.data?.some((item: any) => item.isLmsTraining === true)
  ) {
    return true;
  }
  return false;
});

onMounted(async () => {
  await loadDatas();
});
</script>

<template>
  <div class="flex items-center justify-between">
    <h3 class="heading_3_demi text-primary-color">Participants</h3>
    <div class="flex items-center gap-2">
      <SearchBar
        v-model="paginate.paginateOptions.value.search"
        @update:model-value="paginate.searchNavigateWithoutSettings($event)"
      />
      <AppButton icon="invite" label="Ajouter" @click="handleAddAttendees()" />
    </div>
  </div>
  <div v-if="isLoadingRef">
    <IndexLayoutSkeleton :filters="1" :actions="2" :lane="9" add pagination />
  </div>
  <IndexLayout
    ref="learningPathAttendeesMultiSelect"
    :empty="!isLoadingRef && !isErrorRef && !learningPathAttendeesFullDatas?.data?.length"
    :rows="learningPathAttendeesFullDatas as IPaginationApi<any>"
    track-by="userId"
    :headers="visibleHeaders"
    :nav="getNav()"
    :checkbox="true"
    :select-all="learningPathAttendeesSelectAll"
    :item-per-page="paginate.paginateOptions.value.limit as number"
    :search-message="searchMessage as string"
    url="/users?userId=__KEY__"
    :item-keys="['userId']"
    :resize="true"
    :pagination-key="PaginationKeysEnum.LEARNING_PATH_ATTENDEES_MODAL_PAGINATION"
    :page-container="true"
    :max-height="maxHeight"
    @page-navigate="paginate.pageNavigateWithoutSettings($event)"
    @tab-navigate="paginate.tabNavigateWithoutSettings($event)"
    @sort="paginate.sortColumnWithoutSettings($event)"
    @click-row="clickRow"
  >
    <template #row="{ row }">
      <AttendeesView :row="row"></AttendeesView>
    </template>
    <template #empty>
      <EmptyState message="Aucun participant ajouté." icon="target-menu" size="medium">
        <div class="flex gap-1">
          <span class="body_regular text-medium-grey-color">
            Vous pouvez en ajouter en
            <a class="underline cursor-pointer text-primary-blue-color" @click="handleAddAttendees()"> cliquant ici</a>
            !
          </span>
        </div>
      </EmptyState>
    </template>
  </IndexLayout>

  <div class="footer z-[104] pointer-events-none">
    <FooterActions
      v-if="showActionFooter && learningPathAttendeesFullDatas?.data?.length"
      v-model="learningPathAttendeesSelectAll"
      :footer-actions="footersActionsAttendees"
      :items-name="`Utilisateurs`"
      :excluded-items-count="learningPathAttendeesMultiSelect?.excludedItems?.length || 0"
      :selected-items-count="learningPathAttendeesMultiSelect?.selectedItems?.length || 0"
      :total-count="learningPathAttendeesFullDatas?.meta?.count || 0"
    ></FooterActions>
  </div>

  <DeleteAttendeesModal
    v-if="learningPathDeleteAttendeesModal"
    :disable="disableChoiceActions"
    @cancel="handleCancelDeleteAttendees"
    @confirm="handleKeepSubscriptions($event)"
  />
</template>

<style lang="scss" scoped>
.footer {
  bottom: 50px;
  position: fixed;
  width: 100%;
  left: 50%;
  transform: translateX(-50%);
}
</style>
